import type {
    ToastSwipeDismissedConstantType,
    ToastFocusInsideConstantType,
    ToastStackLayoutConstantType,
    ToastSwipeAmountConstantType,
    ToastExitOffsetConstantType,
    ToastAlignSignConstantType,
    ToastSwipeSignConstantType,
    ToastOffsetConstantType,
    ToastStackTargetType,
    ToastSide
} from "./type";
import {
    getDirectionSign
} from "../../helpers/direction";

export const TOAST_STACK = {
    fillerOverlap: 1,
    scaleStep: 0.05,
    visibleCount: 3,
    dropRatio: 0.4
};

export const TOAST_SWIPE = {
    dampingDistance: 20,
    dampingBase: 1.5,
    velocity: 0.11,
    threshold: 45,
    slop: 8
};

export const getToastStackRestTarget = ({
    layers
}: {
    layers: NCoreUIKit.ShapeConfigs["layers"];
}): ToastStackTargetType => ({
    zIndex: layers.raised,
    isVisible: true,
    isFront: true,
    translateX: 0,
    translateY: 0,
    opacity: 1,
    scaleX: 1,
    scaleY: 1,
    cover: 0
});

export const getToastLift = ({
    side
}: {
    side: ToastSide;
}): number => {
    return side === "top" ? 1 : -1;
};

export const getToastAlignSign = ({
    direction,
    align
}: ToastAlignSignConstantType): number => {
    const directionSign = getDirectionSign({
        direction
    });

    if(align === "start") {
        return directionSign;
    }

    if(align === "end") {
        return -directionSign;
    }

    return 0;
};

export const getToastHiddenOffset = ({
    height,
    side
}: ToastOffsetConstantType): number => {
    return -getToastLift({
        side
    }) * height;
};

export const getToastExitOffset = ({
    isCollapsedBack,
    height,
    side
}: ToastExitOffsetConstantType): number => {
    if(isCollapsedBack) {
        return height * TOAST_STACK.dropRatio;
    }

    return getToastHiddenOffset({
        height,
        side
    });
};

export const getToastStackLayout = ({
    isExpanded,
    direction,
    layers,
    align,
    sizes,
    side,
    ids,
    gap
}: ToastStackLayoutConstantType): Record<string, ToastStackTargetType> => {
    const lift = getToastLift({
        side
    });

    const alignSign = getToastAlignSign({
        direction,
        align
    });

    const frontId = ids[0];

    const frontSize = frontId ? sizes[frontId] : undefined;

    const layout: Record<string, ToastStackTargetType> = {};

    let expandedOffset = 0;

    ids.forEach((id, index) => {
        const size = sizes[id];

        const scale = 1 - TOAST_STACK.scaleStep * index;

        const isFit = !!size && !!frontSize && size.width > 0 && size.height > 0 && frontSize.width > 0 && frontSize.height > 0;

        const widthDelta = isFit ? frontSize.width - size.width : 0;

        const heightDelta = isFit ? frontSize.height - size.height : 0;

        const fitX = isFit ? frontSize.width / size.width : 1;

        const fitY = isFit ? frontSize.height / size.height : 1;

        layout[id] = {
            translateY: isExpanded ? lift * expandedOffset : lift * (gap * index + heightDelta / 2),
            translateX: isExpanded ? 0 : alignSign * widthDelta / 2,
            opacity: index < TOAST_STACK.visibleCount ? 1 : 0,
            isVisible: index < TOAST_STACK.visibleCount,
            zIndex: layers.raised + ids.length - index,
            cover: isExpanded || index === 0 ? 0 : 1,
            scaleX: isExpanded ? 1 : scale * fitX,
            scaleY: isExpanded ? 1 : scale * fitY,
            isFront: index === 0
        };

        expandedOffset += (size?.height ?? 0) + gap;
    });

    return layout;
};

export const getToastSwipeSign = ({
    direction,
    align,
    axis,
    side
}: ToastSwipeSignConstantType): number => {
    if(axis === "y") {
        return -getToastLift({
            side
        });
    }

    return -getToastAlignSign({
        direction,
        align
    });
};

export const getToastSwipeAmount = ({
    delta,
    sign
}: ToastSwipeAmountConstantType): number => {
    if(delta * sign >= 0) {
        return delta;
    }

    return delta / (TOAST_SWIPE.dampingBase + Math.abs(delta) / TOAST_SWIPE.dampingDistance);
};

export const getIsToastSwipeDismissed = ({
    elapsed,
    amount,
    sign
}: ToastSwipeDismissedConstantType): boolean => {
    if(amount * sign <= 0) {
        return false;
    }

    const distance = Math.abs(amount);

    const velocity = elapsed > 0 ? distance / elapsed : 0;

    return distance >= TOAST_SWIPE.threshold || velocity > TOAST_SWIPE.velocity;
};

export const getIsToastFocusInside = ({
    relatedTarget,
    currentTarget
}: ToastFocusInsideConstantType): boolean => {
    if(typeof Node === "undefined" || !(currentTarget instanceof Node) || !(relatedTarget instanceof Node)) {
        return false;
    }

    return currentTarget.contains(relatedTarget);
};

export const getIsToastFocusVisible = ({
    target
}: {
    target: unknown;
}): boolean => {
    if(typeof target !== "object" || target === null || !("matches" in target) || typeof target.matches !== "function") {
        return true;
    }

    try {
        return target.matches(":focus-visible");
    } catch {
        return true;
    }
};
